import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { useNumericParams, useWatchEntityUpdates } from '@domain-group/react-hooks'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import quoteAndFinanceTabBarItems from '@sections/Client/packs/Campaign/utils/generateQuoteAndFinanceTabBarItems'

import useCampaign from '@hooks/useCampaign'

import CampaignFee from '@components/CampaignFee'
import Activity from '@components/EntityActivity/Activity'
import defaultRequestOptions from '@components/EntityActivity/defaultRequestOptions'
import FinanceApplications from '@components/FinanceApplications'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PendingInvoices from '@components/PendingInvoices'
import ReconciliationTotals from '@components/ReconciliationTotals'
import StatementHistory from '@components/StatementHistory'

import PageContext from '@contexts/pageContext'

import useCampaignAccounting from '@hooks/useCampaignAccounting'

import StatementHistoryBlankState from '../StatementHistoryBlankState'

// Activity keys shown in the statement history activity log
const STATEMENT_ACTIVITY_KEY_PREFIXES = 'invoice.,credit_note.,credit_note_item.,campaign_contribution.'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        defaultSelectLabel: 'Show All',
        style,
      },
      key: 'itemType',
      label: 'Select Option',
      values: [
        { key: 'bill', label: 'Bill' },
        { key: 'campaign_balance', label: 'Campaign Balance' },
        { key: 'credit', label: 'Credit' },
        { key: 'invoice', label: 'Invoice' },
        { key: 'payment', label: 'Payment' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {
   itemType?: string,
   string?: string,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const { campaignId } = useNumericParams()

  const { campaign, campaign: { address }, loading: loadingCampaign } = useCampaign({ id: campaignId })

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showAddAccountingItemModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  // Reload the activity log whenever an accounting item changes
  const { updatedEntities } = useWatchEntityUpdates([
    'campaignContributions', 'creditNoteItems', 'creditNotes', 'invoices',
  ])
  const activityRefetchKey = [
    updatedEntities.campaignContributions,
    updatedEntities.creditNoteItems,
    updatedEntities.creditNotes,
    updatedEntities.invoices,
  ].join('-')

  const campaignAccountingPayload = useCampaignAccounting({
    agencyId: campaign?.agency_id,
    campaignId,
    filters: { ...pageFilters },
    performHttpRequests: !!campaignId,
  })

  const {
    callbacks, filteredStatementItems, hasFilteredStatementItems, hasStatementItems, loading,
  } = campaignAccountingPayload

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Accounting</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showAddAccountingItemModal({ campaign })} size="medium">
            Create Item
          </Button>
        )}
        activeTabBarItemKey="accounting"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={quoteAndFinanceTabBarItems(campaignId)}
        title="Accounting"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.accounting.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasStatementItems && (
              <StatementHistoryBlankState />
            )}

            {!loading && !loadingCampaign && hasStatementItems && (
              <StatementHistory
                callbacks={callbacks}
                campaign={campaign}
                hasFilteredStatementItems={hasFilteredStatementItems}
                // @ts-expect-error TS2322
                items={filteredStatementItems}
              />
            )}

            {!!campaignId && (
              <DashboardModule title="Statement Activity">
                <Activity
                  boxProps={{
                    marginTop: 0, paddingBottom: 'large', paddingTop: 'medium', paddingLeft: 'large',
                  }}
                  filters={{ campaign_records: true, key_prefixes: STATEMENT_ACTIVITY_KEY_PREFIXES, limit: 50 }}
                  refetchKey={activityRefetchKey}
                  requestOptions={defaultRequestOptions.activity}
                  subjectId={campaignId}
                  subjectType="Campaign"
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.accounting.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />

            {!loadingCampaign && (
              <ReconciliationTotals
                campaign={campaign}
                displayKeys={[
                  'approved_invoice_total',
                  'credit_note_total',
                  'contribution_total',
                  'received_contribution_total',
                  'campaign_balance',
                ]}
              />
            )}

            <CampaignFee campaign={campaign} />

            <PendingInvoices accountingPayload={campaignAccountingPayload} campaign={campaign} />

            <FinanceApplications campaignId={campaignId} />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}
export default PageContent
